import React, { useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import {
  createAddressbookContact
} from '#action/addressbook/addressbook-contact';
import {
  valuesToAddressbookFormat,
  formatDeliveryValues,
  withAddressbookContactId
} from '#component/shipment/form/helper';
import { DEFAULT_DISTANCE_DEVIATION } from '#component/shipment/form/delivery/distance-deviation';
import { setDisplayFields as setDisplayFieldsAction, editDelivery } from '#action/shipment/delivery';
import {
  StyledDialog,
  StyledIconButton,
  StyledDialogTitle,
  StyledDialogContent
} from './styles';
import Form from './form';
import EmptyAddressbooksMsg from './emptyAddressbooksMsg';
import { convertDistance } from '#helper';

const AddressbookDialog = props => {
  const {
    setDialogOpen,
    formIndex,
    isOpen,
    isDeliveryFormPrestine,
    formValues,
    deliveryFormStates
  } = props;

  const dispatch = useDispatch();
  const { id } = useParams();
  const measurementSystem = useSelector(s =>
    s.account && s.account.measurementSystem, shallowEqual);
  const { addressbooks } = useSelector(s => s.addressbooks, shallowEqual);
  const displayFields = useSelector(s => s.delivery, shallowEqual);

  const { address, company, locations, radius, formattedAddress } = formValues;
  const initialValues = useMemo(() => ({
    companyName: company?.companyName ?? '',
    firstName: company?.firstName ?? '',
    lastName: company?.lastName ?? '',
    addressbook: addressbooks?.[0] || null,
    locations: locations ?? [],
    radius: radius || convertDistance(DEFAULT_DISTANCE_DEVIATION, 'metric', measurementSystem),
    address: {
      address1: address?.address1 ?? '',
      address2: address?.address2 ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      country: address?.country ?? '',
      zipCode: address?.zipCode ?? ''
    },
    formattedAddress: formattedAddress ?? ''
  }), [address, company, locations, radius, formattedAddress, addressbooks, measurementSystem]);

  const handleCloseDialog = () => setDialogOpen(false);

  const handleFormSubmit = async values => {
    const transformedValues = valuesToAddressbookFormat(
      { ...initialValues, ...values },
      measurementSystem
    );
    const { id: contactId } = await dispatch(createAddressbookContact(transformedValues));

    if (!contactId) {
      return;
    }

    dispatch(setDisplayFieldsAction(withAddressbookContactId(displayFields, formIndex, contactId)));

    if (id && isDeliveryFormPrestine) {
      const formattedDelivery = formatDeliveryValues(deliveryFormStates, measurementSystem);
      dispatch(editDelivery(id, withAddressbookContactId(formattedDelivery, formIndex, contactId)));
    }

    await handleCloseDialog();
  };

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleCloseDialog}
      onClick={e => e.stopPropagation()}
    >
      <StyledDialogTitle>
        Save contact to addressbook
        <StyledIconButton
          aria-label="close"
          onClick={handleCloseDialog}
        >
          <CloseIcon />
        </StyledIconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        {!addressbooks?.length ? <EmptyAddressbooksMsg /> : <Form
          {...props}
          initialValues={initialValues}
          handleCloseDialog={handleCloseDialog}
          onSubmit={handleFormSubmit}
          measurementSystem={measurementSystem}
        />}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default AddressbookDialog;
